import { EditorUtils } from '@progress/kendo-react-editor';

// Attributes worth keeping from pasted HTML. Everything else (class, id,
// Word/Google Docs metadata) is presentational or editor-specific.
const KEPT_ATTRIBUTES = new Set([
  'href',
  'target',
  'title',
  'src',
  'alt',
  'width',
  'height',
  'colspan',
  'rowspan',
  'start',
  'type',
  // ProseMirror's clipboard marker, needed to paste partial blocks back correctly.
  'data-pm-slice',
]);

// Alignment and list numbering styles map to editor tools; fonts, sizes,
// colors and spacing are dropped so the site styling applies.
const KEPT_STYLES = ['text-align', 'list-style-type'];

// Content copied inside the editor itself may also carry indentation from the
// Indent tool, which is stored as margin-left.
const KEPT_STYLES_INTERNAL = [...KEPT_STYLES, 'margin-left'];

// Left/start is the default alignment; Word for the web sets it explicitly on
// every paragraph, which would override a site-wide alignment.
const DEFAULT_ALIGNMENTS = new Set(['left', 'start']);

const REMOVED_ELEMENTS = 'style, script, title, meta, link';

const BLOCK_ELEMENTS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'table',
  'blockquote',
  'pre',
  'hr',
]);

// Word for the web wraps every paragraph in its own <div>. Such wrappers only
// add nesting, so unwrap divs that contain nothing but block elements.
const containsOnlyBlocks = (element: Element): boolean =>
  Array.from(element.childNodes).every((node) =>
    node.nodeType === Node.ELEMENT_NODE
      ? BLOCK_ELEMENTS.has((node as Element).tagName.toLowerCase())
      : !node.textContent?.trim(),
  );

// Browsers ignore whitespace and control characters inside a URL scheme,
// e.g. "java\nscript:", so strip them before checking.
const hasAllowedScheme = (url: string, schemes: RegExp): boolean => {
  // eslint-disable-next-line no-control-regex
  const scheme = /^([^/?#]*?):/.exec(url.replace(/[\u0000- ]/g, ''));
  return scheme === null || schemes.test(scheme[1]);
};

const LINK_SCHEMES = /^(https?|mailto|tel)$/i;
const IMAGE_SCHEMES = /^https?$/i;

const isBoldWeight = (weight: string): boolean =>
  weight === 'bold' || weight === 'bolder' || Number(weight) >= 600;

const isNormalWeight = (weight: string): boolean =>
  weight === 'normal' ||
  weight === 'lighter' ||
  (Number(weight) > 0 && Number(weight) < 600);

const unwrap = (element: Element) => {
  element.replaceWith(...Array.from(element.childNodes));
};

const wrapChildren = (element: Element, tagName: string) => {
  const wrapper = element.ownerDocument.createElement(tagName);
  wrapper.append(...Array.from(element.childNodes));
  element.append(wrapper);
};

// Google Docs and many websites express bold/italic/underline as inline styles
// on spans. Turn those into tags the editor understands before the span and its
// styles are removed.
const convertStylesToTags = (element: HTMLElement) => {
  const { fontWeight, fontStyle } = element.style;
  const decoration =
    element.style.textDecorationLine || element.style.textDecoration;
  if (isBoldWeight(fontWeight)) wrapChildren(element, 'strong');
  if (fontStyle === 'italic' || fontStyle === 'oblique')
    wrapChildren(element, 'em');
  if (decoration.includes('underline')) wrapChildren(element, 'u');
  if (decoration.includes('line-through')) wrapChildren(element, 's');
};

const cleanAttributes = (element: HTMLElement, keptStyles: string[]) => {
  const styles = keptStyles
    .map((name) => [name, element.style.getPropertyValue(name)])
    .filter(
      ([name, value]) =>
        value && !(name === 'text-align' && DEFAULT_ALIGNMENTS.has(value)),
    )
    .map(([name, value]) => `${name}: ${value};`)
    .join(' ');

  for (const { name, value } of Array.from(element.attributes)) {
    if (name === 'style') continue;
    const unsafeHref =
      name === 'href' && !hasAllowedScheme(value, LINK_SCHEMES);
    if (!KEPT_ATTRIBUTES.has(name) || unsafeHref) {
      element.removeAttribute(name);
    }
  }

  if (styles) {
    element.setAttribute('style', styles);
  } else {
    element.removeAttribute('style');
  }
};

const cleanElement = (element: HTMLElement, keptStyles: string[]) => {
  const tagName = element.tagName.toLowerCase();

  // Images pasted from Word arrive as file:// or base64 data: URLs, which would
  // bloat the stored HTML; images have to be inserted by URL instead.
  if (tagName === 'img') {
    const src = element.getAttribute('src') ?? '';
    if (!src || !hasAllowedScheme(src, IMAGE_SCHEMES)) {
      element.remove();
      return;
    }
  }

  // Google Docs wraps the whole selection in <b style="font-weight:normal">.
  if (
    (tagName === 'b' || tagName === 'strong') &&
    isNormalWeight(element.style.fontWeight)
  ) {
    unwrap(element);
    return;
  }

  if (tagName === 'span' || tagName === 'font') {
    convertStylesToTags(element);
    unwrap(element);
    return;
  }

  if (tagName === 'div' && containsOnlyBlocks(element)) {
    unwrap(element);
    return;
  }

  cleanAttributes(element, keptStyles);
};

// Content pasted from Word, Outlook, Google Docs or websites carries inline
// fonts, sizes and Mso* classes that override the site styling. Keep only the
// structure (paragraphs, headings, lists, tables, bold/italic, links, images)
// and drop all presentational attributes.
export const cleanPastedHtml = (pastedHtml: string): string => {
  const isInternal = pastedHtml.includes('data-pm-slice');

  // Kendo's helpers re-serialize top-level comments as plain text, so comments
  // must be gone before convertMsLists. sanitize() needs Word's
  // <!--StartFragment--> markers, so it runs first.
  const withoutComments = EditorUtils.sanitize(pastedHtml).replace(
    /<!--[\s\S]*?-->/g,
    '',
  );
  const html = EditorUtils.pasteCleanup(withoutComments, {
    convertMsLists: true,
  });

  // A <template> parses inertly: no scripts run and no images load.
  const template = document.createElement('template');
  template.innerHTML = html;
  const content = template.content;

  content.querySelectorAll(REMOVED_ELEMENTS).forEach((el) => el.remove());

  const keptStyles = isInternal ? KEPT_STYLES_INTERNAL : KEPT_STYLES;
  // querySelectorAll returns a static list, so unwrapping or wrapping elements
  // while iterating still visits every original element.
  content
    .querySelectorAll<HTMLElement>('*')
    .forEach((element) => cleanElement(element, keptStyles));

  return template.innerHTML;
};
